import React from 'react';
import { ArrowRight, Newspaper } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PressLogo {
  image: string;
  alt: string;
  link: string;
  width?: number;
  height?: number;
}

interface PressLogosProps {
  logos: PressLogo[];
  className?: string;
}

const PressLogos: React.FC<PressLogosProps> = ({ logos, className }) => {
  const handleLogoClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className={`bg-secondary py-12 relative ${className}`}>
      {/* Make this overlay click-through */}
      <div className="absolute inset-0 bg-gold-gradient opacity-5 pointer-events-none"></div>

      <div className="container mx-auto px-4 flex flex-col items-center">
        <h3 className="text-center text-primary/80 uppercase tracking-wider text-sm mb-8 font-medium">
          AS FEATURED ON:
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-10 items-center justify-items-center max-w-5xl mx-auto">
          {logos.map((logo, index) => (
            <div
              key={index}
              onClick={() => handleLogoClick(logo.link)}
              className="flex items-center justify-center hover:opacity-75 transition-opacity cursor-pointer"
              role="link"
              aria-label={`Visit article in ${logo.alt}`}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLogoClick(logo.link); }}
            >
              <img
                src={logo.image}
                alt={logo.alt}
                style={{
                  height: logo.height || (
                    logo.alt === "NPR" ? '75px' :
                    logo.alt === "NBC New York" ? '70px' :
                    logo.alt === "AM New York" ? '45px' :
                    logo.alt === "Time Out New York" ? '65px' :
                    logo.alt === "New York Post" ? '60px' :
                    '60px'
                  ),
                  maxWidth: logo.width || (
                    logo.alt === "NPR" ? '220px' :
                    logo.alt === "NBC New York" ? '200px' :
                    logo.alt === "AM New York" ? '140px' :
                    logo.alt === "Time Out New York" ? '180px' :
                    logo.alt === "New York Post" ? '180px' :
                    '180px'
                  ),
                  objectFit: 'contain',
                }}
                loading="lazy"
                onError={(e) => {
                  console.error(`Failed to load image for ${logo.alt}`);
                  e.currentTarget.src = "/placeholder.svg";
                }}
              />
            </div>
          ))}
        </div>

        {/* CTA for In The News page */}
        <div className="mt-8 text-center">
          <Link
            to="/in-the-news"
            className="inline-flex items-center font-medium text-accent hover:text-gold transition-colors group bg-transparent border-0 px-4 py-2 hover:bg-accent/10 rounded cursor-pointer"
          >
            <Newspaper size={18} className="mr-2" />
            <span>See all press coverage</span>
            <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PressLogos;
